import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProfileViewModel } from "@/viewModels/useProfileViewModel";
import { AppLayout } from "@/ui/layouts";
import { AvatarUploadSection } from "@/ui/components/AvatarUploadSection";
import { WalletManagementSection } from "@/ui/components/WalletManagementSection";
import { SocialLinksSection } from "@/ui/components/SocialLinksSection";
import {
  Button,
  ConfirmationInputModal,
  ErrorState,
  PageTitle,
  ProfileSkeleton,
} from "@/ui/components";
import { EditIcon } from "@/ui/components/icons";
import { cn } from "@/lib/utils";

/**
 * ProfilePage Component
 *
 * STRICT MVVM PATTERN - ZERO LOGIC
 * - Pure rendering only
 * - Single useEffect(onMount, [onMount]) ONLY
 * - All logic in useProfileViewModel
 *
 * Responsive design:
 * - Mobile (< 768px): max-w-[21.4375rem] (343px)
 * - Tablet+ (≥ 768px): max-w-[43.75rem] (700px)
 */
export default function ProfilePage() {
  const { t } = useTranslation();
  const { state, actions, computed, onMount } = useProfileViewModel();
  const usernameInputRef = useRef<HTMLInputElement>(null);
  const [localUsername, setLocalUsername] = useState("");
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);

  // ONLY allowed useEffect - for mount lifecycle
  useEffect(onMount, [onMount]);

  // Sync local username with profile state
  useEffect(() => {
    if (state.profile?.username) {
      setLocalUsername(state.profile.username);
    }
  }, [state.profile?.username]);

  // Auto-focus username input when entering edit mode
  useEffect(() => {
    if (isEditingUsername && usernameInputRef.current) {
      usernameInputRef.current.focus();
    }
  }, [isEditingUsername]);

  // Handle username blur with validation error handling
  const handleUsernameBlur = async (value: string) => {
    try {
      await actions.updateUsername(value);
      setUsernameError(null); // Clear error on success
      setIsEditingUsername(false); // Exit edit mode only on success
    } catch (error) {
      // Keep edit mode active, show error
      // Redux thunk rejectWithValue returns string, not Error object
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "string"
          ? error
          : "Invalid username";
      setUsernameError(errorMessage);
      // Input stays focused for user to correct
    }
  };

  // Loading state
  if (state.isLoading && !state.profile) {
    return (
      <AppLayout>
        <PageTitle>{t("profile.title", "My profile")}</PageTitle>
        <ProfileSkeleton />
      </AppLayout>
    );
  }

  // Error state
  if (state.error && !state.profile) {
    return (
      <AppLayout>
        <PageTitle>{t("profile.title", "My profile")}</PageTitle>
        <ErrorState
          message={t(
            "profile.errorLoading",
            "An error occurred while loading your profile. Please retry."
          )}
          onRetry={actions.refreshProfile}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Page Title */}
      <PageTitle>{t("profile.title", "My profile")}</PageTitle>

      {/* Avatar Section */}
      <div className="mb-8 mr-9">
        <AvatarUploadSection
          avatarUrl={state.profile?.avatar}
          isLoading={state.isLoading}
          uploadProgress={state.uploadProgress}
          initials={computed.initials}
          onUpload={actions.uploadAvatar}
          onDelete={actions.deleteAvatar}
        />
      </div>

      {/* Username Field with Edit Button */}
      <div className="mb-4">
        <div className="flex items-center gap-2">
          {!isEditingUsername ? (
            <>
              <p className="flex-1 font-montserrat text-[0.9375rem] font-bold text-gray-500 leading-[1.25rem] text-center">
                {state.profile?.username || localUsername}
              </p>
              <Button
                variant="secondary"
                size="icon"
                onClick={() => setIsEditingUsername(true)}
                aria-label={t("profile.editUsername") || "Edit username"}
              >
                <EditIcon size={12} />
              </Button>
            </>
          ) : (
            <input
              ref={usernameInputRef}
              id="username"
              type="text"
              value={localUsername}
              className={cn(
                "flex-1 px-3 py-2 border rounded-md",
                "font-montserrat text-sm text-gray-900 bg-white",
                usernameError ? "border-danger" : "border-gray-300"
              )}
              onBlur={(e) => handleUsernameBlur(e.target.value)}
              onChange={(e) => {
                setLocalUsername(e.target.value);
                setUsernameError(null); // Clear error as user types
              }}
              autoFocus
            />
          )}
        </div>
        {usernameError && (
          <p className="text-danger text-xs mt-1 font-montserrat">
            {usernameError}
          </p>
        )}
      </div>

      {/* Social Links Section */}
      <div className="mb-8 mt-8">
        <SocialLinksSection
          socialLinks={state.profile?.socialLinks || []}
          onAddSocialLink={actions.addSocialLink}
          onDeleteSocialLink={actions.deleteSocialLink}
          onToggleVisibility={actions.toggleSocialLinkVisibility}
          canAddSocialLink={computed.canAddSocialLink}
          isLoading={state.isLoading}
        />
      </div>

      {/* Wallet Management Section */}
      <div className="mb-8">
        <WalletManagementSection
          wallets={state.profile?.wallets || []}
          onAddWallet={actions.addWallet}
          onDeleteWallet={actions.deleteWallet}
          onToggleVisibility={actions.toggleWalletVisibility}
          canAddWallet={computed.canAddWallet}
          isLoading={state.isLoading}
        />
      </div>

      {/* Export Data and Delete Account Buttons */}
      <div className="mt-12 flex flex-col gap-4">
        {/* Export Data Button */}
        <Button
          variant="primary-dark"
          size="primary-dark"
          onClick={actions.openExportModal}
          disabled={state.isExporting}
          className="w-full"
        >
          {t("profile.exportData")}
        </Button>

        {/* Delete Account Button */}
        <Button
          variant="primary-dark"
          size="primary-dark"
          onClick={actions.openDeleteModal}
          disabled={state.isDeleting}
          className="w-full"
        >
          {t("profile.deleteAccount")}
        </Button>
      </div>

      {/* Export Data Modal */}
      {state.showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-[21.4375rem] w-full">
            <ConfirmationInputModal
              message={t("profile.exportModal.message")}
              confirmationWord={t("profile.exportModal.confirmationWord")}
              cancelText={t("profile.exportModal.cancel")}
              confirmText={t("profile.exportModal.confirm")}
              onCancel={actions.closeExportModal}
              onConfirm={actions.confirmExport}
              isLoading={state.isExporting}
            />
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {state.showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-[21.4375rem] w-full">
            <ConfirmationInputModal
              message={t("profile.deleteModal.message")}
              confirmationWord={t("profile.deleteModal.confirmationWord")}
              cancelText={t("profile.deleteModal.cancel")}
              confirmText={t("profile.deleteModal.confirm")}
              onCancel={actions.closeDeleteModal}
              onConfirm={actions.confirmDelete}
              isLoading={state.isDeleting}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
